/**
 * Note-name-to-frequency conversion, standard 12-tone equal temperament tuned
 * to A4 = 440Hz. This is what turns `composition.ts`'s note *names* (data)
 * into the numbers `AudioContext.createOscillator().frequency` actually
 * wants — pure arithmetic, no Web Audio API involved, so it is unit-testable
 * on its own.
 */

const NOTE_INDEX: Readonly<Record<string, number>> = {
  C: 0,
  'C#': 1,
  D: 2,
  'D#': 3,
  E: 4,
  F: 5,
  'F#': 6,
  G: 7,
  'G#': 8,
  A: 9,
  'A#': 10,
  B: 11,
};

/** Every note name `noteFreq` accepts. */
export type NoteName = keyof typeof NOTE_INDEX;

/** `NOTE_INDEX.A` — pulled out as its own constant rather than read through the index signature, so it is a definite `number`, not `number | undefined`. */
const A_INDEX = 9;

/**
 * `name`/`octave` to Hz. Octave numbering follows scientific pitch notation
 * (A4 = 440Hz, middle C = C4), the same convention every DAW and the MIDI
 * spec use.
 */
export function noteFreq(name: string, octave: number): number {
  const idx = NOTE_INDEX[name];
  if (idx === undefined) throw new Error(`noteFreq: unknown note name "${name}"`);
  const semitonesFromA4 = idx - A_INDEX + (octave - 4) * 12;
  return 440 * 2 ** (semitonesFromA4 / 12);
}

/** True when `name` is one `noteFreq` recognises. Useful for validating composition data. */
export function isNoteName(name: string): name is NoteName {
  return Object.hasOwn(NOTE_INDEX, name);
}
